import { useEffect, FC, useMemo } from 'react';
import { useStore as useInventorizeStore } from 'routes/inventorize_meter/useStore';
import { useStore as useInstallStore } from 'routes/installation/useStore';
import { getInstallMeterDbApi } from 'api/indexed_db/installMeterDb';
import { getInventoryMetersDbApi } from 'api/indexed_db/inventoryMetersDb';
import { SerializedInventoryLocation } from '@enmon/commons/lib/tools';
import { useServices } from 'services/useServices';
import { useReplaceMeterStore } from 'routes/meter_replacement/useStore';
import { StyledHeaderTitleWrapper } from './styled/TitleWrapper';
import { StyledHeaderSubtitleWrapper } from './styled/SubtitleWrapper';
import { StyledHeaderContentWrapper } from './styled/ContentWrapper';
import { HeaderRightSide } from './RightSide';
import { HeaderLeftSide } from './LeftSide';
import { StyledHeader } from './styled/HeaderWrapper';

interface HeaderProps {
  subtitle: React.ReactNode;
}

const DEFAULT_LOCATION_NAME = 'ENMON Inventory App' as const;

const getLocationName = (location: SerializedInventoryLocation | null) => {
  const name = location?.name || DEFAULT_LOCATION_NAME;
  return <span className={name.length > 30 ? 'title-animated' : undefined}>{name}</span>;
};

export const Header: FC<HeaderProps> = ({ subtitle }) => {
  const { location, setQueuedMetersCount: setQueuedInventorizeMetersCount } = useInventorizeStore();
  const { setQueuedMetersCount: setQueuedInstallMetersCount } = useInstallStore();
  const { setQueuedMetersCount: setQueuedReplaceMetersCount } = useReplaceMeterStore();
  const {
    indexedDb: { replaceMeterDb },
  } = useServices();

  useEffect(() => {
    const installMeterDb = getInstallMeterDbApi();
    const inventoryMetersDb = getInventoryMetersDbApi();
    const setQueuedMetersCount = async () => {
      const installMetersCount = await installMeterDb.countNotSynchronized();
      const inventoryMetersCount = await inventoryMetersDb.countNotSynchronized();
      const replaceMetersCount = await replaceMeterDb.countNotSynchronized();
      setQueuedInventorizeMetersCount(inventoryMetersCount);
      setQueuedInstallMetersCount(installMetersCount);
      setQueuedReplaceMetersCount(replaceMetersCount);
    };
    setQueuedMetersCount();
  }, [setQueuedInventorizeMetersCount, setQueuedInstallMetersCount, setQueuedReplaceMetersCount, replaceMeterDb]);

  const locationName = useMemo(() => getLocationName(location), [location]);

  return (
    <StyledHeader>
      <StyledHeaderContentWrapper>
        <HeaderLeftSide />
        <StyledHeaderTitleWrapper>{locationName}</StyledHeaderTitleWrapper>
        <StyledHeaderSubtitleWrapper>{subtitle}</StyledHeaderSubtitleWrapper>
        <HeaderRightSide />
      </StyledHeaderContentWrapper>
    </StyledHeader>
  );
};
